"use client"

import * as React from "react"
import {
  BookOpen,
  Bot,
  Command,
  Frame,
  PieChart,
  Settings2,
  SquareTerminal,
  Boxes,
  Building2,
  CircleUser,
  Factory,
  Landmark,
  Puzzle,
  TableRowsSplit,
  Dna,
  Cpu,
  Pill,
  Microscope,
  LayoutList,
} from "lucide-react"
import { useEffect, useState } from 'react';
import { Skeleton } from "@/components/ui/skeleton"
import Link from "next/link"

import { NavMain } from "@/components/nav-main"
import { NavProjects } from "@/components/nav-projects"
import { NavUser } from "@/components/nav-user"
import { TeamSwitcher } from "@/components/team-switcher"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from "@/components/ui/sidebar"
import { getCurrentUser } from "@/utils/session"
import { title } from "process";
import api from "@/utils/api"
import { useTeam } from "@/components/team-context"

interface User {
  organizations?: string[];
  nickname?: string;
  username?: string;
  avatar?: string;
  access_token?: string;
  refresh_token?: string;
  userid?: string;
  [key: string]: string | string[] | undefined;
}

// 新增积分余额接口
interface AccountBalance {
  organization_id: string;
  balance: number;
  organization_name: string | null;
}

// 组织数据接口
interface Organization {
  organizationId: string;
  name: string;
  description: string;
  createdAt?: string;
  amount?: number;
}

interface OrganizationResponse {
  code: number;
  message: string;
  organization_info: Organization[];
}

// 创建一个图标数组供选择
export const organizationIcons = [
  Command,
  Boxes,
  Building2,
  CircleUser,
  Factory,
  Landmark,
  Frame,
  PieChart
];


export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { activeTeam, setActiveTeam } = useTeam();
  
  // 避免 hydration 错误：首次渲染时使用默认值
  const [userData, setUserData] = React.useState({
    name: "User",
    email: "user@example.com", 
    avatar: "",
  })

  // 避免 hydration 错误：首次渲染使用空数组，等待真实数据
  const [initialTeams, setInitialTeams] = React.useState<any[]>([]);

  const [teams, setTeams] = useState(initialTeams);
  const [categories, setCategories] = useState<Array<{title: string, url: string, icon: any}>>([]);
  const [navMainItems, setNavMainItems] = useState<Array<{
    title: string, 
    url: string, 
    icon: any, 
    isActive?: boolean,
    items?: Array<{title: string, url: string}>
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [navMainLoading, setNavMainLoading] = useState(true);

  // 从API实时获取用户组织数据
  React.useEffect(() => {
    const fetchUserOrganizations = async () => {
      if (typeof window === 'undefined') return;
      
      try {
        // 检查用户是否已登录
        const user = getCurrentUser() as User | undefined;
        if (!user?.access_token) {
          return;
        }

        // 从API获取用户组织信息
        const response = await api.get('/api/user/get_this_user_organization_info');
        const responseData = response as unknown as OrganizationResponse;
        
        if (responseData.code === 0 && responseData.organization_info) {
          const orgList = responseData.organization_info;
          
          if (orgList.length > 0) {
            // 将API返回的组织转换为团队格式
            const userTeams = orgList.map((org, index) => ({
              name: org.name,
              logo: organizationIcons[index % organizationIcons.length],
              logoName: organizationIcons[index % organizationIcons.length].name,
              plan: "Loading..." // 初始显示加载中
            }));
            

            setInitialTeams(userTeams);
            setTeams(userTeams);
          } else {
            // 如果没有组织，使用默认的kinet团队
            const defaultTeam = [{
              name: 'kinet',
              logo: Command,
              logoName: 'command',
              plan: "Free"
            }];
            setInitialTeams(defaultTeam);
            setTeams(defaultTeam);
          }
        } else {
          console.error('API response error:', responseData);
          // API出错时使用默认团队
          const defaultTeam = [{
            name: 'kinet',
            logo: Command,
            logoName: 'command',
            plan: "Free"
          }];
          setInitialTeams(defaultTeam);
          setTeams(defaultTeam);
        }
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
        
        // 获取组织失败时，尝试从localStorage获取
        const user = getCurrentUser() as User | undefined;
        if (user?.organizations && user.organizations.length > 0) {
          const userTeams = user.organizations.map((orgName, index) => ({
            name: orgName,
            logo: organizationIcons[index % organizationIcons.length],
            logoName: organizationIcons[index % organizationIcons.length].name,
            plan: "Loading..."
          }));
          setInitialTeams(userTeams);
          setTeams(userTeams);
        } else {
          // 最后备选：使用默认团队
          const defaultTeam = [{
            name: 'kinet',
            logo: Command,
            logoName: 'command',
            plan: "Free"
          }];
          setInitialTeams(defaultTeam);
          setTeams(defaultTeam);
        }
      }
    };
    
    fetchUserOrganizations();
  }, []); // 只在组件挂载时执行一次

  // 用户数据更新 - 客户端挂载后更新真实用户信息
  React.useEffect(() => {
    // 只在客户端执行
    if (typeof window !== 'undefined') {
      const currentUser = getCurrentUser()
      if (currentUser) {
        setUserData({
          name: currentUser.nickname ?? "User",
          email: currentUser.username ?? "user@example.com",
          avatar: "",
        })
      }
    }
  }, []) // 只在组件挂载时执行一次

  // 分离状态：独立管理积分信息（每次都重新请求，确保实时性）
  const [creditsMap, setCreditsMap] = React.useState<{[teamName: string]: string}>({});
  const [isCreditsLoading, setIsCreditsLoading] = React.useState(false);

  // 获取积分信息 - 每次都重新请求以确保数据新鲜
  React.useEffect(() => {
    const fetchCredits = async () => {
      if (isCreditsLoading || !teams.length) return;
      
      // 检查是否有非kinet团队需要获取balance
      const realTeams = teams.filter(team => team.name !== 'kinet');
      if (realTeams.length === 0) return;
      
      try {
        setIsCreditsLoading(true);
        
        // 为每个非默认组织获取积分余额
        const creditsPromises = realTeams
          .map(async (team) => {
            try {
              const response = await api.get<AccountBalance>(`/bill/billing/accounts/${team.name}/balance`);
              
              if (response && typeof response === 'object') {
                const balance = (response as any).balance;
                if (balance !== undefined && balance !== null) {
                  return {
                    teamName: team.name,
                    credits: `${balance} Credits`
                  };
                }
              }
              return { teamName: team.name, credits: "N/A" };
            } catch (error) {
              console.error(`Failed to fetch credits for ${team.name}:`, error);
              return { teamName: team.name, credits: "N/A" };
            }
          });
        
        const creditsResults = await Promise.all(creditsPromises);
        const newCreditsMap: {[teamName: string]: string} = {};
        creditsResults.forEach(result => {
          newCreditsMap[result.teamName] = result.credits;
        });
        
        setCreditsMap(newCreditsMap);
      } catch (error) {
        console.error("Failed to fetch credits:", error);
      } finally {
        setIsCreditsLoading(false);
      }
    };
    
    fetchCredits();
  }, [teams.length]); // 依赖团队数量变化

  // 合并团队数据和积分信息
  const teamsWithCredits = React.useMemo(() => {
    return teams.map(team => ({
      ...team,
      plan: creditsMap[team.name] || team.plan
    }));
  }, [teams, creditsMap]);

  // 同步 activeTeam 状态，确保 activeTeam 包含最新的 balance 信息
  React.useEffect(() => {
    if (activeTeam && teamsWithCredits.length > 0) {
      const updatedActiveTeam = teamsWithCredits.find(team => team.name === activeTeam.name);
      if (updatedActiveTeam && updatedActiveTeam.plan !== activeTeam.plan) {
        setActiveTeam(updatedActiveTeam);
      }
    }
  }, [teamsWithCredits, activeTeam?.name, setActiveTeam]);

  // 请求状态管理，防止重复请求
  const [isAccessDataLoading, setIsAccessDataLoading] = React.useState(false);
  const [lastRequestedTeam, setLastRequestedTeam] = React.useState<string | null>(null);

  // 统一获取模型和工具权限（不使用缓存）
  React.useEffect(() => {
    const fetchAccessData = async () => {
      try {
        if (!activeTeam || activeTeam.name === 'kinet') {
          setLoading(false);
          setCategories([]);
          setNavMainLoading(false);
          setNavMainItems([]);
          setLastRequestedTeam(null);
          return;
        }

        // 防重复请求：如果正在请求中，则跳过
        if (isAccessDataLoading) {
          return;
        }

        // 防止重复请求同一团队
        if (lastRequestedTeam === activeTeam.name) {
          return;
        }

        console.log(`[API Request] Fetching access data for team: ${activeTeam.name}`);
        setIsAccessDataLoading(true);
        setLastRequestedTeam(activeTeam.name);
        setLoading(true);
        setNavMainLoading(true);
        
        // 同时发送两个请求
        const [modelsResponse, utilitiesResponse] = await Promise.all([
          api.post('/api/access_model', {
            organization: activeTeam.name
          }),
          api.post('/api/access_model', {
            organization: activeTeam.name,
            utility: true
          })
        ]);
        
        // 处理模型权限
        let resultCategories: any[] = [];
        
        if (modelsResponse && modelsResponse.data && Array.isArray(modelsResponse.data)) {
          // 将API返回的模型名称映射到UI所需的categories格式
          const modelIconMap: Record<string, any> = {
            'LexMolProp': { title: 'Drug-like Properties', icon: Microscope, url: '/platform/calculate/lexprop' },
            'LexDock': { title: 'Molecular Docking', icon: Puzzle, url: '/platform/calculate/lexdock' },
            'Affinity': { title: 'Affinity Prediction', icon: TableRowsSplit, url: '/platform/calculate/lexaffinity' },
            'LexHelix': { title: 'P-L Complex Prediction', icon: Dna, url: '/platform/calculate/lexhelix' },
            'Generation': { title: 'Molecular Generation', icon: Cpu, url: '/platform/calculate/lexdock' },
            'Clinical': { title: 'Clinical Prediction', icon: Pill, url: '/platform/taskss' },
            'Task': { title: 'Job Management', icon: LayoutList, url: '/platform/tasks' },
          };
          
          // 从API返回的模型中筛选出已知模型
          const mappedCategories = modelsResponse.data
            .filter((modelName: string) => modelName in modelIconMap)
            .map((modelName: string) => {
              const config = modelIconMap[modelName];
              return {
                title: config.title,
                url: config.url,
                icon: config.icon
              };
            });
          
          // 确保始终包含Task项
          const hasTask = mappedCategories.some(category => category.title === 'Job Management');
          if (!hasTask) {
            mappedCategories.push({
              title: 'Job Management',
              url: '/platform/tasks',
              icon: LayoutList
            });
          }
          
          resultCategories = mappedCategories;
        } else {
          // 即使API响应无效，也要确保有Task项
          resultCategories = [{
            title: 'Job Management',
            url: '/platform/tasks',
            icon: LayoutList
          }];
        }
        
        setCategories(resultCategories);

        // 处理工具权限
        let navMainItems: any[] = [];
        
        if (utilitiesResponse && utilitiesResponse.data && Array.isArray(utilitiesResponse.data)) {
          // 定义utilities的映射关系
          const utilitiesMap = {
            // Draw相关权限
            'Draw.Protein': {
              parent: 'Draw',
              icon: SquareTerminal,
              title: 'Protein',
              url: '#'
            },
            'Draw.Ligand': {
              parent: 'Draw',
              icon: SquareTerminal,
              title: 'Ligand',
              url: '/platform/utilities/ligand_2d'
            },
            
            // Database相关权限
            'Database.Drug': {
              parent: 'Database',
              icon: Bot,
              title: 'Drug',
              url: '/platform/utilities/database/drug'
            },
            'Database.Molecule': {
              parent: 'Database',
              icon: Bot,
              title: 'Drug',
              url: '/platform/utilities/database/drug'
            },
            'Database.pKa': {
              parent: 'Database',
              icon: Bot,
              title: 'pKa',
              url: '/platform/utilities/database/pka'
            },
            
            // Settings相关权限
            'Settings.General': {
              parent: 'Settings',
              icon: Settings2,
              title: 'General',
              url: '#'
            },
            'Settings.Team': {
              parent: 'Settings',
              icon: Settings2,
              title: 'Team',
              url: '/platform/admin'
            },
            
            // 新增管理页面
            'Admin.User': {
              parent: 'Admin',
              icon: Settings2,
              title: 'User Management',
              url: '/platform/admin'
            },
            'Admin.Organization': {
              parent: 'Admin',
              icon: Settings2,
              title: 'Organization Management',
              url: '/platform/admin'
            }
          };
          
          // 按父级分组utilities
          const groupedUtilities: { [parent: string]: any[] } = {};
          
          utilitiesResponse.data.forEach((utilityKey: string) => {
            const utility = utilitiesMap[utilityKey as keyof typeof utilitiesMap];
            if (utility) {
              if (!groupedUtilities[utility.parent]) {
                groupedUtilities[utility.parent] = [];
              }
              groupedUtilities[utility.parent].push({
                title: utility.title,
                url: utility.url,
                icon: utility.icon
              });
            }
          });
          
          // 将分组的utilities转换为navMain格式
          const parentOrder: Record<string, number> = {
            Draw: 0,
            Database: 1,
            Admin: 2,
          };

          navMainItems = Object.entries(groupedUtilities)
            .map(([parent, items], index) => ({ parent, items, index }))
            .sort((a, b) => {
              const aOrder = parentOrder[a.parent] ?? Number.MAX_SAFE_INTEGER;
              const bOrder = parentOrder[b.parent] ?? Number.MAX_SAFE_INTEGER;
              if (aOrder !== bOrder) {
                return aOrder - bOrder;
              }
              // 其余未指定分组保持后端返回时的首次出现顺序
              return a.index - b.index;
            })
            .map(({ parent, items }) => {
            const dedupedItems = items.filter(
              (item, index, arr) =>
                arr.findIndex(
                  (candidate) =>
                    candidate.title === item.title && candidate.url === item.url
                ) === index
            );

            return {
              title: parent,
              url: dedupedItems[0]?.url || '#', // 使用第一个子项的URL或默认#
              icon: dedupedItems[0]?.icon || Settings2,
              isActive: true,
              items: dedupedItems
            };
          });
        }

        setCategories(resultCategories);
        setNavMainItems(navMainItems);

      } catch (error) {
        console.error("Failed to fetch access data:", error);
        // 错误时设置为空数组
        setCategories([]);
        setNavMainItems([]);
      } finally {
        setLoading(false);
        setNavMainLoading(false);
        setIsAccessDataLoading(false);
      }
    };
    
    fetchAccessData();
  }, [activeTeam?.name]); // 只依赖团队名称，避免对象引用变化


  const data = {
    user: userData,
    teams: teamsWithCredits,
    navMain: navMainItems,
    categories: categories,
  }

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <TeamSwitcher teams={data.teams} />
      </SidebarHeader>
      <SidebarContent className="overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-300 scrollbar-track-transparent">
        {loading ? (
          // 使用 Skeleton 组件创建加载骨架屏
          <div className="px-4 py-2">
            <Skeleton className="h-5 w-24 mb-3" /> {/* 类别标题的骨架 */}
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4 rounded-sm" /> {/* 图标骨架 */}
                  <Skeleton className="h-4 w-28" /> {/* 项目名称骨架 */}
                </div>
              ))}
            </div>
          </div>
        ) : (
          categories.length > 0 && <NavMain categorie_name="Calculate" items={data.categories} />
        )}
        
        {navMainLoading ? (
          // 为navMain添加Skeleton
          <div className="px-4 py-2 mt-4">
            <Skeleton className="h-5 w-24 mb-3" /> {/* 类别标题的骨架 */}
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={`nav-${i}`} className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4 rounded-sm" /> {/* 图标骨架 */}
                  <Skeleton className="h-4 w-28" /> {/* 项目名称骨架 */}
                </div>
              ))}
            </div>
          </div>
        ) : (
          navMainItems.length > 0 && <NavMain categorie_name="Utilities" items={data.navMain} />
        )}
      </SidebarContent>
      <SidebarFooter>
        <NavUser user={data.user} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
